"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { OnpageRow, OnpageStarted, Project } from "@/lib/api/types";
import { onpageCost } from "@/lib/costs";
import { formatCount, formatDateTime } from "@/lib/format";
import {
  type TypeFilter,
  aiLabel,
  deltaClass,
  deltaLabel,
  matchesFilter,
  matchesQuery,
  openTarget,
  scoreClass,
  typeLabel,
} from "@/lib/onpage";

import { OnpageTabs } from "./onpage-tabs";
import { useOnpageRequirements } from "./use-onpage-requirements";

export const POLL_MS = 3000;
const FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "weekly", label: "Wochen-Check" },
  { value: "manual", label: "Manuell" },
];

export function OnpageList() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Suche, Filter und Rückgängig gehören zum Projekt
  return project ? <ListView key={project.id} project={project} /> : null;
}

function describe(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

function ListView({ project }: { project: Project }) {
  const router = useRouter();
  const { requirements } = useOnpageRequirements(project.id);
  const [items, setItems] = useState<OnpageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TypeFilter>("all");
  const [removed, setRemoved] = useState<OnpageRow | null>(null);
  const [rerun, setRerun] = useState<OnpageRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(
    (silent: boolean) =>
      api<OnpageRow[]>(`/api/onpage/analyses?project_id=${project.id}`)
        .then((list) => {
          setItems(list);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/onpage"));
            return;
          }
          if (!silent) setError(describe(caught));
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load(false);
  }, [load, attempt]);

  // Läuft eine Analyse, aktualisiert sich die Liste von selbst
  const running = (items ?? []).some((row) => row.latest?.state === "running");
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => void load(true), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, load]);

  const visible = useMemo(
    () => (items ?? []).filter((row) => matchesFilter(row, filter) && matchesQuery(row, query)),
    [items, query, filter],
  );
  const weeklyCount = (items ?? []).filter((row) => row.weekly).length;

  const remove = async (row: OnpageRow) => {
    setActionError(null);
    try {
      await api(`/api/onpage/pages/${row.page_id}`, { method: "DELETE" });
      setItems((current) => (current ?? []).filter((r) => r.page_id !== row.page_id));
      setRemoved(row);
    } catch (caught) {
      setActionError(describe(caught));
    }
  };

  const undo = async () => {
    if (!removed) return;
    setActionError(null);
    try {
      await api<OnpageRow>(`/api/onpage/pages/${removed.page_id}/restore`, { method: "POST" });
      setRemoved(null);
      setAttempt((current) => current + 1); // Reihenfolge und Stand vom Server
    } catch (caught) {
      setActionError(describe(caught));
    }
  };

  const confirmRerun = async () => {
    if (!rerun) return;
    setActionError(null);
    try {
      const started = await api<OnpageStarted>(`/api/onpage/pages/${rerun.page_id}/rerun`, { method: "POST" });
      setRerun(null);
      router.push(`/onpage/${started.snapshot_id}`);
    } catch (caught) {
      setActionError(describe(caught));
    }
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Onpage-Optimierung · Projekt {project.domain}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Onpage-Analysen</h1>
        </div>
        <Link
          href="/onpage/neu"
          className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-sm font-medium text-white"
        >
          Neue Analyse
        </Link>
      </header>

      <OnpageTabs active="list" count={items?.length ?? null} />

      {removed && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-md bg-ink px-4 py-2.5 text-sm text-white">
          <span className="flex-1">Analyse „{removed.title ?? removed.path}“ gelöscht</span>
          <button
            type="button"
            onClick={() => void undo()}
            className="h-11 rounded-md border border-[#5a636c] px-3 text-[13px]"
          >
            Rückgängig
          </button>
        </div>
      )}
      {rerun && (
        <div
          role="group"
          aria-label="Neu analysieren bestätigen"
          className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-white px-4 py-3 text-sm"
        >
          <span className="min-w-[260px] flex-1">
            <strong className="font-medium">„{rerun.title ?? rerun.path}“ neu analysieren?</strong> Es entsteht ein
            neuer Snapshot, der alte bleibt für den Vergleich erhalten.{" "}
            {requirements ? `Verbrauch: ${onpageCost(1, requirements.estimate_per_url)}.` : ""}
          </span>
          <button
            type="button"
            onClick={() => void confirmRerun()}
            className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white"
          >
            Neu analysieren
          </button>
          <button
            type="button"
            onClick={() => setRerun(null)}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]"
          >
            Abbrechen
          </button>
        </div>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}

      <section aria-labelledby="analyses-title" className="rounded-lg border border-line bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e6e9e7] px-5 py-3">
          <h2 id="analyses-title" className="text-base font-semibold">
            Bestehende Analysen
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="onpage-search" className="sr-only">
              Analysen durchsuchen
            </label>
            <input
              id="onpage-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="URL oder Titel suchen …"
              className="h-11 w-60 max-w-full rounded-md border border-field px-3 text-sm"
            />
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={filter === f.value}
                onClick={() => setFilter(f.value)}
                className={`h-11 rounded-md border px-3 text-[13px] ${
                  filter === f.value ? "border-accent bg-accent text-white" : "border-field bg-white"
                }`}
              >
                {f.label}
                {f.value === "weekly" ? ` · ${formatCount(weeklyCount)}` : ""}
                {f.value === "manual" ? ` · ${formatCount((items?.length ?? 0) - weeklyCount)}` : ""}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div role="alert" className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm text-danger">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setAttempt((current) => current + 1)}
              className="h-11 rounded-md border border-danger bg-white px-3.5 text-sm"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {items === null && !error && <p className="px-5 py-6 text-sm text-muted">Analysen werden geladen …</p>}

        {items !== null && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="w-full px-5 py-2.5 font-medium">Seite</th>
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">GEO-Readability</th>
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">AI-Zitierungen</th>
                    <th className="px-3 py-2.5 text-right font-medium">Offene Maßnahmen</th>
                    <th className="whitespace-nowrap px-3 py-2.5 font-medium">Zuletzt analysiert</th>
                    <th className="whitespace-nowrap py-2.5 pl-3 pr-4 text-right font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <Row key={row.page_id} row={row} onRerun={setRerun} onRemove={(r) => void remove(r)} />
                  ))}
                </tbody>
              </table>
            </div>
            {visible.length === 0 && (
              <p className="px-5 py-6 text-sm text-muted">
                {items.length === 0
                  ? "Noch keine Analysen. Mit „Neue Analyse“ geht es los."
                  : "Keine Analysen gefunden."}
              </p>
            )}
            <p className="border-t border-neutral-bg px-5 py-3 text-xs text-muted">
              Automatische Analysen stammen aus dem wöchentlichen Check (folgt mit S16). Jede Analyse speichert einen
              Snapshot für den Vorher-Nachher-Vergleich.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

/** „4 von 4“ mit „Plattformen“ darunter: zwei gewollte Zeilen statt eines Umbruchs mitten im Satz. */
function Citations({ cited, total }: { cited: number | null; total: number }) {
  if (cited === null) return <>{aiLabel(cited, total)}</>;
  return (
    <>
      <span className="block">{`${cited} von ${total}`}</span>
      <span className="block text-xs text-muted">Plattformen</span>
    </>
  );
}

/** Datum und Uhrzeit untereinander. */
function Stamp({ iso }: { iso: string }) {
  const [day, time] = formatDateTime(iso).split(", ");
  return (
    <>
      <span className="block whitespace-nowrap">{day}</span>
      {time && <span className="block whitespace-nowrap text-xs text-muted">{time} Uhr</span>}
    </>
  );
}

/** Pfad mit Umbruchmöglichkeit nach jedem Schrägstrich, sonst bleibt er zusammen. */
function breakAtSlashes(path: string) {
  return path.split("/").map((part, index, all) => (
    <Fragment key={index}>
      {index < all.length - 1 ? `${part}/` : part}
      {index < all.length - 1 ? <wbr /> : null}
    </Fragment>
  ));
}

function Row({
  row,
  onRerun,
  onRemove,
}: {
  row: OnpageRow;
  onRerun: (row: OnpageRow) => void;
  onRemove: (row: OnpageRow) => void;
}) {
  const done = row.last_done;
  const latest = row.latest;
  const name = row.title ?? row.path;
  const target = openTarget(row);
  const running = latest?.state === "running";
  const failed = latest?.state === "failed";
  return (
    <tr className="border-t border-neutral-bg align-top">
      <td className="min-w-[260px] px-5 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">{name}</span>
          <span className="font-mono text-xs text-muted">{breakAtSlashes(row.path)}</span>
          <span
            className={`mt-1 w-fit whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${
              row.weekly ? "bg-[#ede7f6] text-[#4a2d7a]" : "bg-neutral-bg text-[#3a424a]"
            }`}
          >
            {typeLabel(row.weekly)}
          </span>
          {row.briefing_keyword && <span className="text-xs text-muted">Briefing: {row.briefing_keyword}</span>}
          {running && (
            <span className="mt-1 w-fit rounded bg-info-bg px-2 py-[3px] text-xs font-medium text-info">
              Analyse läuft …
            </span>
          )}
          {failed && (
            <span role="status" className="mt-1 text-xs text-danger">
              Analyse fehlgeschlagen{latest?.error ? `: ${latest.error}` : ""}
            </span>
          )}
        </div>
      </td>
      <td className="px-2.5 py-3">
        {done && done.score !== null ? (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className={`whitespace-nowrap rounded px-2 py-0.5 font-mono text-[13px] font-semibold ${scoreClass(done.score)}`}>
              {done.score} / 100
            </span>
            <span className={`font-mono text-xs ${deltaClass(row.score_delta)}`}>{deltaLabel(row.score_delta)}</span>
          </div>
        ) : (
          <span className="text-muted">–</span>
        )}
      </td>
      <td className={`whitespace-nowrap px-3 py-3 ${done?.ai_cited === 0 ? "text-danger" : ""}`}>
        {done ? <Citations cited={done.ai_cited} total={done.ai_total} /> : "–"}
      </td>
      <td className="whitespace-nowrap px-2.5 py-3 text-right font-mono text-[13px]">
        {done?.open_actions !== null && done?.open_actions !== undefined ? formatCount(done.open_actions) : "–"}
      </td>
      <td className="whitespace-nowrap px-2.5 py-3 text-[#3a424a]">
        {done ? <Stamp iso={done.finished_at ?? done.created_at} /> : "–"}
      </td>
      <td className="whitespace-nowrap py-1.5 pl-3 pr-4 text-right">
        <div className="flex items-center justify-end gap-1">
        {target !== null && (
          <Link
            href={`/onpage/${target}`}
            aria-label={`Öffnen: ${name}`}
            className="inline-flex h-11 items-center whitespace-nowrap rounded-md border border-field bg-white px-3 text-[13px]"
          >
            Öffnen
          </Link>
        )}
        <button
          type="button"
          onClick={() => onRerun(row)}
          disabled={running}
          aria-label={`Neu analysieren: ${name}`}
          title="Neu analysieren"
          className="inline-flex size-11 items-center justify-center rounded-md text-accent disabled:text-muted"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M20 11a8 8 0 0 0-14.3-4.6M4 5v3.4h3.4M4 13a8 8 0 0 0 14.3 4.6M20 19v-3.4h-3.4" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onRemove(row)}
          aria-label={`Analyse löschen: ${name}`}
          title="Analyse löschen"
          className="inline-flex size-11 items-center justify-center rounded-md text-danger"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
          </svg>
        </button>
        </div>
      </td>
    </tr>
  );
}
